import { t } from "@lingui/macro";
import { enUS, he } from "date-fns/locale";
import { Calendar } from "lucide-react";
import { useState } from "react";
import { recentCallsRange } from "../utils/callsView";
import { Button } from "./ui/button";
import { Calendar as CalendarComponent } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

type Range = { from?: Date; to?: Date };
export function DateRangeFilter({
	dateRange,
	onChange,
	language,
}: {
	dateRange: Range;
	onChange: (range: Range) => void;
	language: string;
}) {
	const [datePickerOpen, setDatePickerOpen] = useState(false);
	const [draftDateRange, setDraftDateRange] = useState(dateRange);
	const format = (date: Date) =>
		date.toLocaleDateString(language === "he" ? "he-IL" : "en-US");
	const dateRangeLabel =
		dateRange.from && dateRange.to
			? `${format(dateRange.from)} – ${format(dateRange.to)}`
			: dateRange.from
				? `${t({ id: "from", message: "From" })} ${format(dateRange.from)}`
				: dateRange.to
					? `${t({ id: "to", message: "To" })} ${format(dateRange.to)}`
					: t({ id: "playbookRuns.filters.dateRange", message: "Date range" });
	return (
		<Popover
			open={datePickerOpen}
			onOpenChange={(open) => {
				if (open) setDraftDateRange(dateRange);
				setDatePickerOpen(open);
			}}
		>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					className="w-full min-w-0 justify-start gap-2"
					aria-label={dateRangeLabel}
				>
					<Calendar className="w-4 h-4 shrink-0" />
					<span className="truncate text-xs sm:text-sm">{dateRangeLabel}</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-auto max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto p-0"
				align="end"
			>
				<div className="flex flex-wrap gap-2 border-b border-border p-3">
					{[
						{
							days: 1,
							label: t({ id: "calls.dates.today", message: "Today" }),
						},
						{
							days: 7,
							label: t({ id: "calls.dates.last7", message: "Last 7 days" }),
						},
						{
							days: 30,
							label: t({ id: "dashboard.last30Days", message: "Last 30 days" }),
						},
						{
							days: 90,
							label: t({ id: "calls.dates.last90", message: "Last 90 days" }),
						},
					].map(({ days, label }) => {
						const range = recentCallsRange(days);
						const selected =
							draftDateRange.from?.getTime() === range.from.getTime() &&
							draftDateRange.to?.getTime() === range.to.getTime();
						return (
							<Button
								key={days}
								size="sm"
								variant={selected ? "secondary" : "ghost"}
								aria-pressed={selected}
								onClick={() => setDraftDateRange(range)}
							>
								{label}
							</Button>
						);
					})}
				</div>
				<div className="p-3 space-y-3 sm:grid sm:grid-cols-2 sm:gap-3 sm:space-y-0">
					<div>
						<label className="text-xs mb-1.5 block">
							{t({ id: "startDate", message: "Start Date" })}
						</label>
						<CalendarComponent
							className="p-2"
							locale={language === "he" ? he : enUS}
							mode="single"
							key={`from-${draftDateRange.from?.getTime()}`}
							defaultMonth={draftDateRange.from}
							selected={draftDateRange.from}
							onSelect={(date) =>
								setDraftDateRange((prev) => ({
									...prev,
									from: date || undefined,
									to: date && prev.to && date > prev.to ? date : prev.to,
								}))
							}
							initialFocus
						/>
					</div>
					<div>
						<label className="text-xs mb-1.5 block">
							{t({ id: "endDate", message: "End Date" })}
						</label>
						<CalendarComponent
							className="p-2"
							locale={language === "he" ? he : enUS}
							mode="single"
							key={`to-${draftDateRange.to?.getTime()}`}
							defaultMonth={draftDateRange.to}
							selected={draftDateRange.to}
							onSelect={(date) =>
								setDraftDateRange((prev) => ({
									...prev,
									to: date || undefined,
								}))
							}
							disabled={(date) =>
								draftDateRange.from ? date < draftDateRange.from : false
							}
						/>
					</div>
				</div>
				<div className="flex justify-end gap-2 border-t border-border p-3">
					<Button
						variant="ghost"
						onClick={() => {
							setDraftDateRange({});
							onChange({});
							setDatePickerOpen(false);
						}}
					>
						{t({ id: "calls.dates.clear", message: "Clear" })}
					</Button>
					<Button
						onClick={() => {
							onChange(draftDateRange);
							setDatePickerOpen(false);
						}}
					>
						{t({ id: "calls.dates.apply", message: "Apply" })}
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	);
}
